export const isIssuerReviewPending = (status: unknown) =>
	status === "under_review" || status === "pending"

/** New responses are authoritative; the fallback preserves rolling deployments. */
export const getEffectiveKycStage = (
	stage: TKycStage | undefined,
	ourKycStatus: TKycStatus | undefined,
): TKycStage => stage ?? (ourKycStatus !== "approved" ? "our_kyc" : "issuer")

export const isIssuerReviewStatus = (data: Pick<
	IKycStatusResponse,
	"stage" | "our_kyc_status" | "provider_kyc_status"
>) =>
	getEffectiveKycStage(data.stage, data.our_kyc_status) === "issuer" &&
	isIssuerReviewPending(data.provider_kyc_status)

export const hasIssuerReviewPending = (
	issuer: IKycIssuer | undefined,
	ourKycStatus?: TKycStatus,
) => {
	if (!issuer) return false
	return issuer.kyc.some(entry => {
		if (!isIssuerReviewPending(entry.status)) return false
		if (entry.stage !== undefined) return entry.stage === "issuer"
		// Old kyc/issuers had no stage. Review metadata was only meaningful for
		// issuer review; callers may query kyc/status when it is absent.
		return ourKycStatus === "approved" || Boolean(issuer.review)
	})
}

export const isIssuerReviewError = (error?: IError) =>
	error?.code === 2613 && isIssuerReviewPending(error.holder_status)

export const reviewFromError = (error: IError): IKycReview | undefined =>
	error.poll_after_seconds !== undefined || error.eta_text !== undefined
		? { poll_after_seconds: error.poll_after_seconds, eta_text: error.eta_text }
		: undefined

export const issuerReviewDelay = (review?: IKycReview): number => {
	const seconds = review?.poll_after_seconds
	// Invalid or overflowing delays must not create a tight polling loop.
	return typeof seconds === "number" && Number.isFinite(seconds) && seconds > 0 && seconds <= 2147483
		? Math.max(1000, seconds * 1000)
		: 30000
}
